<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Verify gate fired when a registered agent tries to generate a
 * branded sales offer PDF for a specific unit.
 */

const { t } = useI18n();

const { unitLabel, userPhoneMasked = '+971 50 ••• ••24' } = defineProps<{
  unitLabel?: string;
  userPhoneMasked?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'verify-start'): void;
  (e: 'change-number'): void;
}>();

const unitLabelText = computed(() => unitLabel ?? t('property.shell.selectedUnitFallback'));

const titleId = useId();
</script>

<template>
  <ModalShell
    accent="green"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="doc"
      accent="green"
    />

    <div class="mb-5 flex items-center gap-3 rounded-xl border border-success/25 bg-success/05 p-3">
      <div class="h-14 w-[42px] shrink-0 overflow-hidden rounded-md">
        <svg
          viewBox="0 0 60 80"
          preserveAspectRatio="xMidYMid meet"
          class="h-full w-full"
        >
          <rect
            width="60"
            height="80"
            rx="4"
            fill="white"
            stroke="#22C55E"
            stroke-width="2"
          />
          <rect
            x="6"
            y="10"
            width="48"
            height="3"
            fill="#0D0F15"
          />
          <rect
            x="6"
            y="18"
            width="32"
            height="2"
            fill="#0D0F15"
            opacity="0.4"
          />
          <rect
            x="6"
            y="36"
            width="48"
            height="2"
            fill="#0D0F15"
            opacity="0.2"
          />
          <rect
            x="6"
            y="42"
            width="48"
            height="2"
            fill="#0D0F15"
            opacity="0.2"
          />
          <rect
            x="6"
            y="48"
            width="40"
            height="2"
            fill="#0D0F15"
            opacity="0.2"
          />
          <rect
            x="6"
            y="60"
            width="20"
            height="14"
            rx="2"
            fill="#FFB648"
          />
          <text
            x="16"
            y="69"
            text-anchor="middle"
            font-size="6"
            font-weight="700"
            fill="#0D0F15"
          >
            {{ t('property.access.modalVerifyOfferPDF.yourLogoPlaceholder') }}
          </text>
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <div class="font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-success">
          {{ t('property.access.modalVerifyOfferPDF.salesOfferLabel', { unit: unitLabelText }) }}
        </div>
        <div class="truncate text-[13.5px] font-semibold text-black-100">
          {{ t('property.access.modalVerifyOfferPDF.brandedWithLogo') }}
        </div>
        <div class="text-[11.5px] text-black-60">
          {{ t('property.access.modalVerifyOfferPDF.sentToClientNote') }}
        </div>
      </div>
    </div>

    <h2
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      {{ t('property.access.modalVerifyOfferPDF.heading') }}
    </h2>
    <p class="mb-5 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalVerifyOfferPDF.body') }}
    </p>

    <ul class="mb-6 space-y-2">
      <CheckBullet accent="green">{{ t('property.access.modalVerifyOfferPDF.bullet1') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyOfferPDF.bullet2') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyOfferPDF.bullet3') }}</CheckBullet>
    </ul>

    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-[14px] font-bold text-white transition-colors hover:bg-success/90"
        @click="emit('verify-start')"
      >
        <span
          >{{ t('property.access.shared.verifyThirtySec') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('close')"
      >
        {{ t('property.access.shared.maybeLater') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      <span dir="ltr">{{ userPhoneMasked }}</span> ·
      <button
        type="button"
        class="font-semibold text-black-80 underline-offset-2 hover:underline"
        @click="emit('change-number')"
      >
        {{ t('property.access.shared.changeNumber') }}
      </button>
    </div>
  </ModalShell>
</template>
